import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  DocumentIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import AboutContentModal from './AboutContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import PhotoGalleryModal from './PhotoGalleryModal';
import PublishControl from './PublishControl';
import TournamentChampionsManager from './TournamentChampionsManager';
import { supabase, getContentSections, createContentItem, updateContentItem, deleteContentItem } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ContentItem, ContentItemType, Photo, PhotoCategory } from '../types/content';

interface ContentSection {
  id: string;
  title: string;
  icon: any;
  items: ContentItem[];
}

interface HeaderContent {
  id: string;
  title: string;
  items: ContentItem[];
}

interface DatabaseContentItem {
  id: string;
  title: string;
  type: ContentItemType;
  content: any;
  status: string;
  section_id: string;
  updated_at: string;
}

interface Tournament {
  id: string;
  name: string;
  start_date: string;
}

function HistoryContentContainer() {
  const [activeSubTab, setActiveSubTab] = useState<'content' | 'champions'>('content');
  const [expandedSection, setExpandedSection] = useState<string | null>('header');
  const [headerSection, setHeaderSection] = useState<HeaderContent>({
    id: '',
    title: 'Page Header',
    items: []
  });
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ sectionId: string; item: ContentItem } | null>(null);
  const [isSectionDeleteModalOpen, setIsSectionDeleteModalOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<ContentSection | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedGalleryItem, setSelectedGalleryItem] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');

  useEffect(() => {
    fetchContent();
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (tournaments.length > 0) {
      fetchContent();
    }
  }, [selectedTournamentId]);

  const fetchTournaments = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date')
        .order('start_date', { ascending: false });

      if (error) throw error;
      if (data) {
        setTournaments(data);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      toast.error('Failed to load tournaments');
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await getContentSections('history');

      if (error) {
        throw error;
      }

      if (data) {
        const processedSections = data
          .filter(section => section.title !== 'Page Header' && section.title !== 'Press')
          .map(section => {
            let items = section.content_items?.map((item: DatabaseContentItem) => ({
              ...item,
              lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0]
            })) || [];

            // Filter gallery items by selected tournament
            if (section.title === 'Gallery' && selectedTournamentId) {
              items = items.filter((item: any) => item.tournament_id === selectedTournamentId);
            }

            return {
              id: section.id,
              title: section.title,
              icon: getIconForSection(section.title),
              items
            };
          });

        setSections(processedSections);

        const headerSectionData = data.find(section => section.title === 'Page Header');
        if (headerSectionData) {
          setHeaderSection({
            id: headerSectionData.id,
            title: headerSectionData.title,
            items: headerSectionData.content_items?.map((item: DatabaseContentItem) => ({
              ...item,
              lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0]
            })) || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const getIconForSection = (title: string) => {
    switch (title.toLowerCase()) {
      case 'champions':
        return TrophyIcon;
      case 'gallery':
        return PhotoIcon;
      default:
        return DocumentIcon;
    }
  };

  const handleSave = async (item: ContentItem) => {
    try {
      let processedContent = item.content;

      if (typeof item.content === 'string' && isValidJson(item.content)) {
        try {
          processedContent = JSON.parse(item.content);
        } catch (e) {
          console.error('Error parsing JSON content:', e);
        }
      }

      const itemData: any = {
        title: item.title,
        type: item.type as ContentItemType,
        content: processedContent,
        status: item.status,
        section_id: selectedSection
      };

      // Add tournament_id for gallery items
      if (item.type === 'gallery' && selectedTournamentId) {
        itemData.tournament_id = selectedTournamentId;
      }

      let error;
      if (item.id) {
        const result = await updateContentItem(item.id, itemData);
        error = result.error;
        
        if (error?.message?.includes('no rows returned')) {
          throw new Error('Content item not found');
        }
      } else {
        const result = await createContentItem(itemData);
        error = result.error;
      }

      if (error) throw error;

      await fetchContent();
      toast.success('Content saved successfully');
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving content:', error);
      toast.error(error.message || 'Failed to save content');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?.item.id) return;

    try {
      const { error } = await deleteContentItem(itemToDelete.item.id);
      if (error) throw error;

      await fetchContent();
      toast.success('Content deleted successfully');
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete?.id) return;
    try {
      const { error } = await supabase
        .from('content_sections')
        .delete()
        .eq('id', sectionToDelete.id);
      if (error) throw error;
      await fetchContent();
      toast.success('Section deleted successfully');
      setIsSectionDeleteModalOpen(false);
      setSectionToDelete(null);
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handlePhotoGalleryEdit = (item: ContentItem) => {
    setSelectedGalleryItem(item);
    setSelectedSection(item.section_id || '');
    setSelectedTournamentId((item as any).tournament_id || '');
    setIsPhotoModalOpen(true);
  };

  const handleToggleStatus = async (item: ContentItem) => {
    const newStatus = item.status === 'Published' ? 'Draft' : 'Published';
    try {
      const { error } = await supabase
        .from('content_items')
        .update({ status: newStatus })
        .eq('id', item.id);
      if (error) throw error;
      await fetchContent();
      toast.success(`Status changed to ${newStatus}`);
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const generateUniqueGalleryTitle = (existingItems: ContentItem[], baseTitle: string): string => {
    let title = baseTitle;
    let counter = 1;
    const existingTitles = new Set(existingItems.map(item => item.title.toLowerCase()));

    if (!existingTitles.has(title.toLowerCase())) {
      return title;
    }

    do {
      title = `${baseTitle} ${counter}`;
      counter++;
    } while (existingTitles.has(title.toLowerCase()));

    return title;
  };

  const handlePhotoGallerySave = async (categories: PhotoCategory[], tournamentId: string | null = null, galleryTitle: string = '') => {
    try {
      const currentSection = sections.find(s => s.id === selectedSection);
      if (!currentSection) throw new Error('Section not found');

      const existingGalleryItems = currentSection.items;

      let title: string;
      let itemId: string | undefined = selectedGalleryItem?.id;

      if (galleryTitle.trim()) {
        title = galleryTitle.trim();
      } else if (selectedGalleryItem?.id) {
        title = selectedGalleryItem.title;
      } else {
        title = generateUniqueGalleryTitle(existingGalleryItems, 'Photo Gallery');
      }

      if (!itemId) {
        const existingItem = existingGalleryItems.find(
          item => item.title.toLowerCase() === title.toLowerCase() && 
          item.section_id === selectedSection
        );
        if (existingItem) {
          itemId = existingItem.id;
        }
      }

      const itemData: any = {
        title,
        type: 'gallery' as ContentItemType,
        content: JSON.stringify(categories),
        status: selectedGalleryItem?.status || 'Draft',
        section_id: selectedSection,
        tournament_id: tournamentId ?? (selectedTournamentId || null),
      };

      let error;
      if (itemId) {
        const result = await updateContentItem(itemId, itemData);
        error = result.error;

        if (error?.message?.includes('no rows returned')) {
          throw new Error('Gallery not found');
        }
      } else {
        const { data: dbConflict } = await supabase
          .from('content_items')
          .select('id')
          .eq('section_id', selectedSection)
          .eq('title', title)
          .maybeSingle();

        if (dbConflict) {
          const result = await updateContentItem(dbConflict.id, itemData);
          error = result.error;
        } else {
          const result = await createContentItem(itemData);
          error = result.error;
        }
      }

      if (error) throw error;

      await fetchContent();
      toast.success('Gallery saved successfully');
      setIsPhotoModalOpen(false);
      setSelectedGalleryItem(null);
    } catch (error: any) {
      console.error('Error saving gallery:', error);
      toast.error(error.message || 'Failed to save gallery');
    }
  };

  const handleEditClick = (sectionId: string, item: ContentItem) => {
    if (item.type === 'gallery') {
      setSelectedSection(sectionId);
      handlePhotoGalleryEdit(item);
    } else {
      setSelectedItem(item);
      setSelectedSection(sectionId);
      setIsModalOpen(true);
    }
  };

  const handleAddClick = (sectionId: string) => {
    setSelectedSection(sectionId);

    if (sectionId.includes('gallery') || sections.find(s => s.id === sectionId)?.title === 'Gallery') {
      if (!selectedTournamentId) {
        toast.error('Please select a tournament first to add a gallery');
        return;
      }
      setSelectedGalleryItem(null);
      setIsPhotoModalOpen(true);
    } else {
      setSelectedItem(null);
      setIsModalOpen(true);
    }
  };

  const handleDeleteClick = (sectionId: string, item: ContentItem) => {
    setItemToDelete({ sectionId, item });
    setIsDeleteModalOpen(true);
  };

  const isValidJson = (str: string): boolean => {
    if (typeof str !== 'string') return false;
    try {
      const firstChar = str.trim()[0];
      if (firstChar !== '{' && firstChar !== '[') return false;
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const renderPreview = (item: ContentItem) => {
    try {
      if (!isValidJson(item.content)) {
        return (
          <div className="mt-2">
            <p className="text-sm text-gray-600">{String(item.content)}</p>
          </div>
        );
      }

      const content = JSON.parse(item.content);

      switch (item.type) {
        case 'champions': {
          const championsData = content.champions || content;
          return (
            <div className="mt-2 grid grid-cols-2 gap-4">
              {Object.entries(championsData).map(([division, champion]: [string, any]) => (
                <div key={division} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img src={champion.image} alt={champion.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-golf-gray">{champion.name}</p>
                    <p className="text-xs text-gray-500">{champion.country}</p>
                  </div>
                </div>
              ))}
            </div>
          );
        }

        case 'gallery': {
          const categories = Array.isArray(content) ? content : [content];
          if (categories.length === 0 || !categories[0]?.photos?.length) {
            return (
              <div className="mt-2">
                <p className="text-sm text-gray-600">No photos available</p>
              </div>
            );
          }
          return (
            <div className="mt-2 grid grid-cols-3 gap-2">
              {categories[0].photos.slice(0, 3).map((photo: Photo, index: number) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden">
                  <img src={photo.url} alt={photo.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          );
        }

        default:
          return (
            <div className="mt-2">
              <p className="text-sm text-gray-600">{JSON.stringify(content)}</p>
            </div>
          );
      }
    } catch (error) {
      console.error('Error parsing content:', error);
      return (
        <div className="mt-2">
          <p className="text-sm text-gray-600">{String(item.content)}</p>
        </div>
      );
    }
  };

  return (
    <div className="space-y-6">
      <PublishControl pageKey="history" pageTitle="History Page" />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveSubTab('content')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeSubTab === 'content'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Content Sections
          </button>
          <button
            onClick={() => setActiveSubTab('champions')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeSubTab === 'champions'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <TrophyIcon className="w-5 h-5" />
              <span>Tournament Champions</span>
            </div>
          </button>
        </div>
      </div>

      {activeSubTab === 'champions' ? (
        <TournamentChampionsManager />
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div
              className="flex items-center justify-between p-6 cursor-pointer"
              onClick={() => toggleSection(headerSection.id)}
            >
              <div className="flex items-center space-x-4">
                <div className="bg-golf-orange/10 p-3 rounded-xl">
                  <DocumentIcon className="w-6 h-6 text-golf-orange" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-golf-gray">{headerSection.title}</h3>
                  <p className="text-sm text-gray-500">{headerSection.items.length} items</p>
                </div>
              </div>
              {expandedSection === headerSection.id ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </div>

            {expandedSection === headerSection.id && (
              <div className="border-t border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {headerSection.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-golf-gray">{item.title}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600">{String(item.content)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <CalendarIcon className="w-4 h-4" />
                              {item.lastUpdated}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleStatus(item); }}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-opacity hover:opacity-75 ${
                                item.status === 'Published'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {item.status === 'Published' ? (
                                <CheckCircleIcon className="w-4 h-4" />
                              ) : (
                                <ClockIcon className="w-4 h-4" />
                              )}
                              {item.status}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(headerSection.id, item);
                                }}
                                className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>

          {sections.map((section) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div
                className="flex items-center justify-between p-6 cursor-pointer"
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-golf-orange/10 p-3 rounded-xl">
                    <section.icon className="w-6 h-6 text-golf-orange" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-golf-gray">{section.title}</h3>
                    <p className="text-sm text-gray-500">{section.items.length} items</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {section.title === 'Gallery' && (
                    <select
                      value={selectedTournamentId}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedTournamentId(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange focus:border-transparent"
                    >
                      <option value="">All Tournaments</option>
                      {tournaments.map((tournament) => (
                        <option key={tournament.id} value={tournament.id}>
                          {tournament.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSectionToDelete(section);
                      setIsSectionDeleteModalOpen(true);
                    }}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddClick(section.id);
                    }}
                    className="p-2 text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                  {expandedSection === section.id ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {expandedSection === section.id && (
                <div className="border-t border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {section.items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-golf-gray">{item.title}</div>
                              <div className="text-xs text-gray-500 capitalize">{item.type}</div>
                            </td>
                            <td className="px-6 py-4">
                              {renderPreview(item)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <CalendarIcon className="w-4 h-4" />
                                {item.lastUpdated}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(item); }}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-opacity hover:opacity-75 ${
                                  item.status === 'Published'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {item.status === 'Published' ? (
                                  <CheckCircleIcon className="w-4 h-4" />
                                ) : (
                                  <ClockIcon className="w-4 h-4" />
                                )}
                                {item.status}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(section.id, item);
                                  }}
                                  className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(section.id, item);
                                  }}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </>
      )}

      <AboutContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        item={selectedItem}
        sectionId={selectedSection}
      />

      <PhotoGalleryModal
        isOpen={isPhotoModalOpen}
        onClose={() => {
          setIsPhotoModalOpen(false);
          setSelectedGalleryItem(null);
        }}
        onSave={handlePhotoGallerySave}
        initialCategories={selectedGalleryItem && isValidJson(selectedGalleryItem.content) ? JSON.parse(selectedGalleryItem.content) : []}
        initialTournamentId={(selectedGalleryItem as any)?.tournament_id || selectedTournamentId || ''}
        initialTitle={selectedGalleryItem?.title || ''}
        onTournamentChange={setSelectedTournamentId}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={itemToDelete?.item.title || ''}
      />
      <DeleteConfirmationModal
        isOpen={isSectionDeleteModalOpen}
        onClose={() => setIsSectionDeleteModalOpen(false)}
        onConfirm={handleDeleteSection}
        title={sectionToDelete?.title || ''}
      />
    </div>
  );
}

export default HistoryContentContainer;